export interface Entitlements {
  adsRemoved: boolean;
  ownedSkins: string[];
  runCount: number;
  consentGiven: boolean;
}

const META_KEY = 'crazy-clock-meta-v1';

const DEFAULT_ENTITLEMENTS: Entitlements = {
  adsRemoved: false,
  ownedSkins: ['sketchbook'],
  runCount: 0,
  consentGiven: false,
};

export function readEntitlements(): Entitlements {
  try {
    const raw = window.localStorage.getItem(META_KEY);
    if (!raw) {
      return { ...DEFAULT_ENTITLEMENTS, ownedSkins: [...DEFAULT_ENTITLEMENTS.ownedSkins] };
    }
    const parsed = JSON.parse(raw) as Partial<Entitlements>;
    const owned = Array.isArray(parsed.ownedSkins)
      ? parsed.ownedSkins.filter((id): id is string => typeof id === 'string')
      : [];
    if (!owned.includes('sketchbook')) {
      owned.push('sketchbook');
    }
    return {
      adsRemoved: parsed.adsRemoved === true,
      ownedSkins: owned,
      runCount: typeof parsed.runCount === 'number' && parsed.runCount > 0 ? Math.floor(parsed.runCount) : 0,
      consentGiven: parsed.consentGiven === true,
    };
  } catch {
    return { ...DEFAULT_ENTITLEMENTS, ownedSkins: [...DEFAULT_ENTITLEMENTS.ownedSkins] };
  }
}

export function writeEntitlements(next: Entitlements): void {
  try {
    window.localStorage.setItem(META_KEY, JSON.stringify(next));
  } catch {
    return;
  }
}

export function updateEntitlements(patch: Partial<Entitlements>): Entitlements {
  const next = { ...readEntitlements(), ...patch };
  writeEntitlements(next);
  return next;
}

export function isAdsRemoved(): boolean {
  return readEntitlements().adsRemoved;
}

export function grantRemoveAds(): void {
  updateEntitlements({ adsRemoved: true });
}

export function ownsSkin(skinId: string): boolean {
  return readEntitlements().ownedSkins.includes(skinId);
}

export function grantSkin(skinId: string): void {
  const current = readEntitlements();
  if (current.ownedSkins.includes(skinId)) {
    return;
  }
  updateEntitlements({ ownedSkins: [...current.ownedSkins, skinId] });
}

export function incrementRunCount(): number {
  const next = readEntitlements().runCount + 1;
  updateEntitlements({ runCount: next });
  return next;
}

export function shouldShowInterstitial(everyRuns: number): boolean {
  if (everyRuns <= 0) {
    return false;
  }
  return readEntitlements().runCount % everyRuns === 0;
}
